// =============================================================================
// SIREPPAR - Texto largo con "Leer más"
// =============================================================================
// El motivo de un documento admite hasta 200 000 caracteres y una observación de
// expediente no tiene tope práctico: la transcripción de un acta o de una carta
// orgánica entra completa. Pintado de una, ese texto empuja todo lo demás fuera de
// la pantalla y hay que hacer scroll a ciegas para volver a los botones.
//
// Este componente muestra un fragmento y deja el resto detrás de "Leer más". No
// recorta datos: el texto completo ya vino del backend y está en memoria, sólo
// cambia cuánto se pinta. Los topes viven en utils/limites.ts.
// =============================================================================

import { ReactNode, useState } from 'react';

/**
 * Corta en el último espacio antes del tope para no partir una palabra al medio.
 * Si no hay ninguno cerca (un pegado sin espacios), corta seco: preferimos el
 * corte feo antes que devolver el texto entero.
 */
function recortar(texto: string, tope: number): string {
  const duro = texto.slice(0, tope);
  const corte = duro.lastIndexOf(' ');
  return corte > tope - 200 ? duro.slice(0, corte) : duro;
}

/**
 * Versión de una sola línea, para subtítulos y renglones de resumen donde no cabe
 * un botón: colapsa los saltos de línea y agrega puntos suspensivos. El texto
 * completo se lee desplegando la fila.
 */
export function resumirEnUnaLinea(texto: string, tope: number): string {
  const plano = texto.replace(/\s+/g, ' ').trim();
  return plano.length <= tope ? plano : `${recortar(plano, tope)}…`;
}

interface Props {
  texto: string;
  /** Caracteres visibles antes de "Leer más". */
  tope: number;
  /**
   * Cómo se pinta el fragmento. Por defecto se pinta como texto plano; se pasa,
   * por ejemplo, `t => <MarkdownDisplay content={t} />` para markdown.
   */
  render?: (fragmento: string) => ReactNode;
  /** `true` cuando el contenido es de bloque (markdown): envuelve en div, no en span. */
  bloque?: boolean;
  className?: string;
}

export default function TextoColapsable({ texto, tope, render, bloque, className }: Props) {
  const [abierto, setAbierto] = useState(false);
  if (!texto) return null;

  const pintar = render ?? ((t: string) => <>{t}</>);

  // Texto corto: ni envoltorio ni botón, se comporta como antes de existir esto.
  if (texto.length <= tope) {
    return className ? <span className={className}>{pintar(texto)}</span> : <>{pintar(texto)}</>;
  }

  const visible = abierto ? texto : `${recortar(texto, tope)}…`;
  const Envoltorio = bloque ? 'div' : 'span';

  return (
    <Envoltorio className={className}>
      {pintar(visible)}
      {/* stopPropagation porque estas líneas viven dentro de filas que se
          despliegan al hacer clic: el botón no debe plegar el archivo. */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setAbierto(!abierto); }}
        className={`text-blue-600 hover:text-blue-800 hover:underline font-medium ${bloque ? 'mt-1 block text-xs' : 'ml-1'}`}
      >
        {abierto ? 'Leer menos' : 'Leer más'}
      </button>
    </Envoltorio>
  );
}
